import React, { useEffect, useMemo, useState } from 'react';
import { buildConflictNotes } from '../../utils/eliminatorConflictNotes';
import {
	getCachedTeamLogoSrc,
	prefetchTeamLogos,
	subscribeTeamLogoCache,
	TEAM_LOGO_CACHE_SIZE
} from '../../utils/teamLogoCache';
import EliminatorConflictNotes from './EliminatorConflictNotes';

const ROSTER_POSITIONS = ['QB', 'RB', 'WR', 'TE'];

const FAVORABILITY = {
	favorable: { label: 'Favorable', dotClass: 'bg-green-500' },
	neutral: { label: 'Neutral', dotClass: 'bg-yellow-400' },
	unfavorable: { label: 'Unfavorable', dotClass: 'bg-red-500' }
};

function normalizeTeamKey(value) {
	return (value || '').toString().trim().toLowerCase();
}

function getWeekMatchup(team, week) {
	if (!team?.weeks) return null;
	return team.weeks[week] || team.weeks[String(week)] || null;
}

function OpponentLogo({ team, sizeClassName = 'h-6 w-6', size = TEAM_LOGO_CACHE_SIZE }) {
	const imageUrl = team?.image_url;
	const [src, setSrc] = useState(() => getCachedTeamLogoSrc(imageUrl, size));

	useEffect(() => {
		if (!imageUrl) {
			setSrc(null);
			return undefined;
		}

		prefetchTeamLogos([imageUrl], size);
		setSrc(getCachedTeamLogoSrc(imageUrl, size));

		return subscribeTeamLogoCache(() => {
			setSrc(getCachedTeamLogoSrc(imageUrl, size));
		});
	}, [imageUrl, size]);

	if (!src) {
		return <div className={`flex-shrink-0 ${sizeClassName}`} aria-hidden="true" />;
	}

	return (
		<img
			src={src}
			alt={`${team.abbreviation || team.name} logo`}
			className={`flex-shrink-0 object-contain ${sizeClassName}`}
			onError={(event) => {
				event.currentTarget.style.display = 'none';
			}}
		/>
	);
}

function MatchupCell({ matchup, opponentTeam, hasSchedule }) {
	if (!hasSchedule) {
		return (
			<span className="text-2xs font-semibold text-gray-400 dark:text-gray-500">—</span>
		);
	}

	if (!matchup) {
		return (
			<span className="text-2xs font-semibold text-gray-400 dark:text-gray-500">BYE</span>
		);
	}

	const venueLabel = matchup.is_home_game ? 'vs' : '@';

	return (
		<div className="flex flex-col items-center justify-center gap-1">
			<span className="text-2xs font-bold uppercase text-gray-500 dark:text-gray-400">
				{venueLabel}
			</span>
			<OpponentLogo team={opponentTeam} />
		</div>
	);
}

function FavorabilityDot({ favorability }) {
	if (!favorability || !FAVORABILITY[favorability]) {
		return (
			<span className="text-2xs font-semibold text-gray-400 dark:text-gray-500">—</span>
		);
	}

	const meta = FAVORABILITY[favorability];
	return (
		<span
			className={`inline-block h-2.5 w-2.5 rounded-full ${meta.dotClass}`}
			title={meta.label}
			aria-label={meta.label}
		/>
	);
}

function RosterPlayerRow({
	entry,
	weeks,
	scheduleTeam,
	teamsById,
	onRemove
}) {
	const favorabilities = entry.favorabilities || {};

	return (
		<div className="flex w-max items-stretch bg-white dark:bg-dark-card">
			<div className="sticky left-0 z-10 flex w-40 flex-none flex-col justify-between border-r border-gray-200 bg-white px-3 py-2 dark:border-dark-border dark:bg-dark-card">
				<div className="min-w-0">
					<div className="truncate text-sm font-semibold text-gray-900 dark:text-dark-text">
						{entry.full_name}
					</div>
					<div className="mt-0.5 text-2xs text-gray-500 dark:text-gray-400">
						#{entry.rank}
						{entry.team_name ? ` · ${entry.team_name}` : ''}
					</div>
				</div>
				<button
					type="button"
					onClick={() => onRemove(entry.id)}
					className="mt-2 self-start rounded px-1.5 py-0.5 text-2xs font-medium text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900 dark:hover:bg-opacity-30"
					aria-label={`Remove ${entry.full_name} from roster`}
				>
					Remove
				</button>
			</div>

			{weeks.map((week) => {
				const matchup = getWeekMatchup(scheduleTeam, week);
				const opponentTeam = matchup ? teamsById[matchup.opponent_id] : null;
				const favorability =
					favorabilities[week] ?? favorabilities[String(week)] ?? null;

				return (
					<div
						key={`roster-${entry.id}-${week}`}
						className="flex w-16 flex-none flex-col items-center justify-center gap-1 border-l border-gray-100 px-1 py-2 dark:border-dark-border"
					>
						<MatchupCell
							matchup={matchup}
							opponentTeam={opponentTeam}
							hasSchedule={Boolean(scheduleTeam)}
						/>
						<FavorabilityDot favorability={favorability} />
					</div>
				);
			})}
		</div>
	);
}

function RosterWeekHeaderRow({ weeks }) {
	return (
		<div className="flex w-max items-stretch border-b border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-gray-800">
			<div className="sticky left-0 z-20 w-40 flex-none border-r border-gray-200 bg-gray-50 px-3 py-2 dark:border-dark-border dark:bg-gray-800">
				<span className="text-2xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
					Player
				</span>
			</div>
			{weeks.map((week) => (
				<div
					key={`roster-week-head-${week}`}
					className="flex w-16 flex-none items-center justify-center border-l border-gray-200 px-1 py-2 text-2xs font-semibold text-gray-600 dark:border-dark-border dark:text-gray-300"
				>
					W{week}
				</div>
			))}
		</div>
	);
}

export default function EliminatorRosterRail({
	isOpen,
	onToggle,
	onSwitchToDraft,
	rosterEntries,
	weeks,
	teamLookup,
	teamsById,
	onRemove,
	onClear
}) {
	const grouped = useMemo(() => {
		const groups = {
			QB: [],
			RB: [],
			WR: [],
			TE: []
		};

		rosterEntries.forEach((entry) => {
			if (groups[entry.position]) {
				groups[entry.position].push(entry);
			}
		});

		return groups;
	}, [rosterEntries]);

	const count = rosterEntries.length;

	if (!isOpen) return null;

	return (
		<aside
			className={`fixed right-0 top-0 z-50 flex h-full max-w-full flex-col border-l border-gray-300 bg-gray-50 shadow-xl dark:border-dark-border dark:bg-dark-bg ${
				count === 0 ? 'w-80' : 'w-max'
			}`}
			aria-label="Eliminator roster"
		>
			<div className="flex items-center justify-between gap-3 border-b border-gray-300 bg-white px-4 py-3 dark:border-dark-border dark:bg-dark-card">
				<div>
					<h2 className="text-base font-semibold text-gray-900 dark:text-dark-text">
						Roster
					</h2>
					<p className="text-xs text-gray-500 dark:text-gray-400">
						{count === 0
							? 'Add players with the + button'
							: `${count} player${count === 1 ? '' : 's'} · week-by-week compare`}
					</p>
				</div>
				<div className="flex items-center gap-2 flex-shrink-0">
					{onSwitchToDraft && (
						<button
							type="button"
							onClick={onSwitchToDraft}
							className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-dark-border dark:text-dark-text dark:hover:bg-gray-800"
						>
							Draft
						</button>
					)}
					{count > 0 && (
						<button
							type="button"
							onClick={onClear}
							className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-dark-border dark:text-dark-text dark:hover:bg-gray-800"
						>
							Clear
						</button>
					)}
					<button
						type="button"
						onClick={onToggle}
						className="rounded-md bg-gray-800 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-gray-700"
					>
						Close
					</button>
				</div>
			</div>

			<div className="flex-1 overflow-auto px-4 py-4">
				{count === 0 ? (
					<div className="rounded-md border border-dashed border-gray-300 px-4 py-10 text-center text-sm text-gray-500 dark:border-dark-border dark:text-gray-400">
						No players yet. Click the <span className="font-semibold">+</span> next to
						a player name in the table.
					</div>
				) : (
					<div className="space-y-5">
						{ROSTER_POSITIONS.map((position) => {
							const players = grouped[position];
							if (!players.length) return null;

							const conflictNotes = buildConflictNotes(
								players,
								weeks,
								teamLookup
							);

							return (
								<section key={position}>
									<h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
										{position}
										<span className="ml-1 font-medium normal-case tracking-normal text-gray-400">
											({players.length})
										</span>
									</h3>
									<div className="w-max divide-y divide-gray-200 rounded-md border border-gray-200 dark:divide-dark-border dark:border-dark-border">
										<RosterWeekHeaderRow weeks={weeks} />
										{players.map((entry) => (
											<RosterPlayerRow
												key={entry.id}
												entry={entry}
												weeks={weeks}
												scheduleTeam={
													teamLookup[normalizeTeamKey(entry.team_name)] ||
													null
												}
												teamsById={teamsById}
												onRemove={onRemove}
											/>
										))}
									</div>
									<EliminatorConflictNotes notes={conflictNotes} />
								</section>
							);
						})}
					</div>
				)}
			</div>
		</aside>
	);
}
